import { Stack, type SxProps, type Theme, useTheme } from "@mui/material";
import {
	type PropsWithChildren,
	type ReactElement,
	type RefObject,
	useLayoutEffect,
	useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { useBottomBarSpacing } from "web_myplann/Shared/Hooks/useBottomBarSpacing";
import { useScrollPositionRestoration } from "web_myplann/Shared/Hooks/useScrollPositionRestoration";
import { MobileLayoutContent } from "web_myplann/Shared/Layouts/MobileLayoutContent";
import { MobileLayoutTopBar } from "web_myplann/Shared/Layouts/MobileLayoutTopBar";

const TOP_GLOW_HEIGHT = 200;

type Props = {
	topBar?: ReactElement;
	bottomBar?: ReactElement;
	disableContentPadding?: boolean;
	fullWidth?: boolean;
	disableBottomBar?: boolean;
	mainSx?: SxProps<Theme>;
	disableBottomPadding?: boolean;
	disableTopPadding?: boolean;
	bgColor?: string;
	topBarBgColor?: string;
	overlayTopBar?: boolean;
	disableBottomBarSpacing?: boolean;
	topGlow?: boolean;
	scrollRef?: RefObject<HTMLDivElement | null>;
	scrollResetKey?: string | number | boolean;
};

export const MyPlannLayout = ({
	children,
	topBar,
	disableContentPadding,
	mainSx,
	disableBottomPadding,
	disableTopPadding,
	bgColor,
	topBarBgColor,
	overlayTopBar,
	disableBottomBarSpacing,
	topGlow,
	scrollRef,
	scrollResetKey,
}: PropsWithChildren<Props>) => {
	const theme = useTheme();
	const bottomBarSpacing = useBottomBarSpacing();
	const hasGoalsRight = useGoalsRightOptional();
	const showGlow = !!topGlow && hasGoalsRight;
	const ownContentRef = useRef<HTMLDivElement>(null);
	const contentRef = scrollRef ?? ownContentRef;
	const { pathname } = useLocation();

	useLayoutEffect(() => {
		contentRef.current?.scrollTo({ top: 0 });
	}, [pathname, scrollResetKey]);
	useScrollPositionRestoration(contentRef, scrollResetKey);
	const baseSx: SxProps<Theme> = {
		...(bgColor && !showGlow ? { background: bgColor } : {}),
		...(!disableBottomBarSpacing ? { paddingBottom: bottomBarSpacing } : {}),
	};
	const contentSx: SxProps<Theme> = [
		baseSx,
		...(Array.isArray(mainSx) ? mainSx : mainSx ? [mainSx] : []),
	];

	return (
		<Stack
			height="100dvh"
			overflow="hidden"
			bgcolor={bgColor}
			sx={[
				showGlow && {
					backgroundImage: theme.gradients.dashboardSkyGlow,
					backgroundRepeat: "no-repeat",
					backgroundSize: `100% ${TOP_GLOW_HEIGHT}px`,
				},
			]}
		>
			<MobileLayoutTopBar
				topBar={topBar}
				disableTopPadding={disableTopPadding}
				overlayTopBar={overlayTopBar}
				topBarSx={{
					...((topBarBgColor ?? bgColor)
						? { backgroundColor: topBarBgColor ?? bgColor }
						: {}),
				}}
			/>

			<MobileLayoutContent
				ref={contentRef}
				disableContentPadding={disableContentPadding}
				disableBottomPadding={disableBottomPadding}
				mainSx={contentSx}
			>
				{children}
			</MobileLayoutContent>
		</Stack>
	);
};
